import { makeStyles, tokens } from '@fluentui/react-components';
import { globalNavRowHeight } from 'globalConstants';

//
// The styles of the main menu: the rail, its rows, the pinned footer, the callout of a pinned group and
// the hosts. Every row height comes from globalNavRowHeight; never write a bare pixel row height.
//
// A row has the same geometry in both presentations (docs/phase-26-admin-ux-fixes.md, D26-3): the icon sits
// at the same place in the icon rail and in the full menu, and a group caption is a row high in both, so
// collapsing or expanding the rail moves no icon; it only shows or hides the labels. The icon rail is
// exactly one icon with the row's padding and the rail's inset wide.
//

/** Marks the menu root, so the callout can tell an event inside the menu from one outside it. */
export const navRailClass = 'skillcontrol-nav-main-rail';

/** On every row of the menu (and of the callout): the hint hover tells rows from captions and padding by it. */
export const navRowClass = 'skillcontrol-nav-main-row';

/** The size of a menu icon, in both presentations. */
export const navIconSize = 24;

// the space between the rail's edge and a row, and between a row's edge and its icon
const navRailInset = 4;
const navRowInline = 8;

export const menuCollapsedWidth = navIconSize + 2 * navRowInline + 2 * navRailInset;
export const menuExpandedWidth = 220;

export const useNavMainStyles = makeStyles({
  rail: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  scroller: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingTop: tokens.spacingVerticalS,
    scrollbarWidth: 'thin',
  },
  scrollerCompact: {
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  footer: {
    flexShrink: 0,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalS,
  },
  nav: {
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    width: '100%',
    rowGap: '2px',
    paddingLeft: `${navRailInset}px`,
    paddingRight: `${navRailInset}px`,
  },
  row: {
    boxSizing: 'border-box',
    minHeight: `${globalNavRowHeight}px`,
    alignItems: 'center',
    paddingTop: `${(globalNavRowHeight - navIconSize) / 2}px`,
    paddingBottom: `${(globalNavRowHeight - navIconSize) / 2}px`,
    paddingLeft: `${navRowInline}px`,
    paddingRight: `${navRowInline}px`,
    // a label never wraps, so a row keeps its height while the rail widens or narrows
    whiteSpace: 'nowrap',
    // Fluent's selected bar stands at the rail's own left edge in both presentations
    '::after': {
      marginInlineStart: `-${navRowInline + navRailInset}px`,
    },
  },
  rowCompact: {
    position: 'relative',
  },
  // the rule that stands for a group caption in the icon rail: as high as the caption, so the rows below it
  // keep their place
  captionCompact: {
    display: 'flex',
    alignItems: 'center',
    height: `${globalNavRowHeight}px`,
  },
  captionRule: {
    width: '100%',
  },
  // the count of an entry (the approvals to decide): at the end of the row, or over the icon in the icon rail
  badge: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  badgeCompact: {
    position: 'absolute',
    top: '2px',
    right: '4px',
  },
  hiddenLabel: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  caption: {
    display: 'flex',
    alignItems: 'center',
    height: `${globalNavRowHeight}px`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  groupToggle: {
    width: '100%',
    justifyContent: 'flex-start',
    minHeight: `${globalNavRowHeight}px`,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
  },
  calloutSurface: {
    padding: tokens.spacingVerticalS,
    minWidth: '200px',
    maxWidth: '280px',
  },
  calloutOverline: {
    display: 'block',
    paddingLeft: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
});

export const useNavHostStyles = makeStyles({
  // the rail widens and narrows with Fluent's motion tokens; nothing moves for a user who asked for less motion
  root: {
    position: 'relative',
    height: '100%',
    transitionProperty: 'width, min-width',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  flyout: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: `${menuExpandedWidth}px`,
    zIndex: 100,
    boxShadow: tokens.shadow16,
  },
});
